import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import DashboardLayout from './DashboardLayout';
import { alertsApi } from '../lib/api';
import './DashboardAlerts.css';

interface Alert {
    id: number;
    title: string;
    description: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success' | 'critical';
    category: 'security' | 'threat' | 'performance' | 'network' | 'system' | 'application' | 'compliance';
    priority: 'low' | 'medium' | 'high' | 'critical';
    status: 'active' | 'acknowledged' | 'investigating' | 'resolved' | 'dismissed' | 'escalated';
    source: string;
    created_at: string;
    updated_at: string;
    created_by_name: string;
    user_id: number;
    assigned_to: number | null;
    assigned_to_name: string | null;
    alert_data: string | null;
    resolution_notes: string | null;
}

const DashboardAlerts: React.FC = () => {
    const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const alertsPerPage = 10;

    // React-select options
    const priorityOptions = [
        { value: 'all', label: 'All Priorities' },
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' }
    ];

    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'active', label: 'Active' },
        { value: 'acknowledged', label: 'Acknowledged' },
        { value: 'investigating', label: 'Investigating' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'dismissed', label: 'Dismissed' },
        { value: 'escalated', label: 'Escalated' }
    ];

    const categoryOptions = [
        { value: 'all', label: 'All Categories' },
        { value: 'security', label: 'Security' },
        { value: 'threat', label: 'Threat' },
        { value: 'performance', label: 'Performance' },
        { value: 'network', label: 'Network' },
        { value: 'system', label: 'System' },
        { value: 'application', label: 'Application' },
        { value: 'compliance', label: 'Compliance' }
    ];

    // Custom styles for react-select
    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.8)',
            border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
            borderRadius: '8px',
            padding: '2px 4px',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.95)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 99999999,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: state.isSelected 
                ? 'rgba(99, 102, 241, 0.2)' 
                : state.isFocused 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'transparent',
            color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            padding: '10px 14px',
            cursor: 'pointer',
            '&:hover': {
                background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
            }
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            padding: '6px',
            '&:hover': {
                color: 'var(--text-primary)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Client-side filtering for better responsiveness
    const filteredAlerts = useMemo(() => {
        let filtered = allAlerts;

        // Apply search filter
        if (debouncedSearchQuery.trim()) {
            const query = debouncedSearchQuery.toLowerCase();
            filtered = filtered.filter(alert =>
                alert.title.toLowerCase().includes(query) ||
                alert.description?.toLowerCase().includes(query) ||
                alert.source?.toLowerCase().includes(query) ||
                alert.message?.toLowerCase().includes(query)
            );
        }

        // Apply priority filter
        if (priorityFilter !== 'all') {
            filtered = filtered.filter(alert => alert.priority === priorityFilter);
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(alert => alert.status === statusFilter);
        }

        // Apply category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(alert => alert.category === categoryFilter);
        }

        return filtered;
    }, [allAlerts, debouncedSearchQuery, priorityFilter, statusFilter, categoryFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredAlerts.length / alertsPerPage);
    const startIndex = (currentPage - 1) * alertsPerPage;
    const paginatedAlerts = filteredAlerts.slice(startIndex, startIndex + alertsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, priorityFilter, statusFilter, categoryFilter]);

    // Initial data fetch
    useEffect(() => {
        const fetchAlerts = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await alertsApi.getAlerts({ limit: 1000 }); // Fetch all alerts for client-side filtering

                console.log(response);

                if (response.success && response.data) {
                    setAllAlerts(response.data.alerts || []);
                } else {
                    throw new Error(response.error || 'Failed to fetch alerts');
                }
            } catch (error) {
                console.error('Error fetching alerts:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch alerts');
                setAllAlerts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAlerts();
    }, []); // Only fetch once on mount

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        // Ensure we're displaying in the user's local timezone
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };

    const getTimeAgo = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        const now = new Date();
        
        // Calculate difference in UTC to avoid timezone issues
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return formatDate(dateString);
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'critical': return '#ef4444';
            case 'high': return '#f59e0b';
            case 'medium': return '#3b82f6';
            case 'low': return '#10b981';
            default: return '#6b7280';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return '#ef4444';
            case 'investigating': return '#f59e0b';
            case 'resolved': return '#10b981';
            case 'dismissed': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'security': return 'ri-shield-line';
            case 'threat': return 'ri-alarm-warning-line';
            case 'performance': return 'ri-speed-line';
            case 'network': return 'ri-wifi-line';
            case 'system': return 'ri-server-line';
            case 'application': return 'ri-code-line';
            case 'compliance': return 'ri-file-shield-line';
            default: return 'ri-alert-line';
        }
    };

    const handleAlertClick = (alert: Alert) => {
        setSelectedAlert(alert);
        setIsDetailModalOpen(true);
    };

    const closeModal = () => {
        setSelectedAlert(null);
        setIsDetailModalOpen(false);
    };
    if (loading) {
        return (
            <DashboardLayout title="Security Alerts">
                <div className="dashboard-alerts-loading">
                    <div className="dashboard-alerts-loading-spinner">
                        <i className="ri-loader-4-line"></i>
                    </div>
                    <div className="dashboard-alerts-loading-text">Loading alerts...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Security Alerts">
                <div className="dashboard-alerts-error">
                    <div className="dashboard-alerts-error-icon">
                        <i className="ri-error-warning-line"></i>
                    </div>
                    <div className="dashboard-alerts-error-content">
                        <h3>Failed to Load Alerts</h3>
                        <p>{error}</p>
                        <button
                            className="dashboard-alerts-retry-btn"
                            onClick={() => window.location.reload()}
                        >
                            <i className="ri-refresh-line"></i>
                            Try Again
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Security Alerts">
            <div className="dashboard-alerts">
                {/* Header */}
                <div className="dashboard-alerts-header">
                    <div className="dashboard-alerts-header-content">
                        <h1 className="dashboard-alerts-title">Security Alerts</h1>
                        <p className="dashboard-alerts-subtitle">
                            Monitor and track security alerts and incidents across your organization
                        </p>
                    </div>
                    <div className="dashboard-alerts-stats">
                        <div className="dashboard-alerts-stat">
                            <div className="dashboard-alerts-stat-value">{allAlerts.length}</div>
                            <div className="dashboard-alerts-stat-label">Total</div>
                        </div>
                        <div className="dashboard-alerts-stat">
                            <div className="dashboard-alerts-stat-value">
                                {allAlerts.filter((a: Alert) => a.status === 'active').length}
                            </div>
                            <div className="dashboard-alerts-stat-label">Active</div>
                        </div>
                        <div className="dashboard-alerts-stat">
                            <div className="dashboard-alerts-stat-value">
                                {allAlerts.filter((a: Alert) => a.priority === 'critical').length}
                            </div>
                            <div className="dashboard-alerts-stat-label">Critical</div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="dashboard-alerts-filters">
                    <div className="dashboard-alerts-filters-row">
                        <div className="dashboard-alerts-search-container">
                            <div className="dashboard-alerts-search">
                                <i className="ri-search-line"></i>
                                <input
                                    type="text"
                                    placeholder="Search alerts by title, description, or source..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`dashboard-alerts-search-input ${debouncedSearchQuery !== searchQuery ? 'searching' : ''}`}
                                />
                                {searchQuery && (
                                    <button
                                        className="dashboard-alerts-search-clear"
                                        onClick={() => setSearchQuery('')}
                                        title="Clear search"
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                                {debouncedSearchQuery !== searchQuery && (
                                    <div className="dashboard-alerts-search-loading">
                                        <i className="ri-loader-4-line rotating"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="dashboard-alerts-filters-secondary">
                        <div className="dashboard-alerts-filter-group">
                            <div className="dashboard-alerts-filter-item">
                                <label className="dashboard-alerts-filter-label">Priority</label>
                                <Select
                                    options={priorityOptions}
                                    value={priorityOptions.find(option => option.value === priorityFilter)}
                                    onChange={(option) => setPriorityFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="dashboard-alerts-filter-select"
                                />
                            </div>
                            
                            <div className="dashboard-alerts-filter-item">
                                <label className="dashboard-alerts-filter-label">Status</label>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(option => option.value === statusFilter)}
                                    onChange={(option) => setStatusFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="dashboard-alerts-filter-select"
                                />
                            </div>
                            
                            <div className="dashboard-alerts-filter-item">
                                <label className="dashboard-alerts-filter-label">Category</label>
                                <Select
                                    options={categoryOptions}
                                    value={categoryOptions.find(option => option.value === categoryFilter)}
                                    onChange={(option) => setCategoryFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="dashboard-alerts-filter-select"
                                />
                            </div>
                            
                            <div className="dashboard-alerts-filter-actions">
                                <button
                                    className="dashboard-alerts-filter-clear"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setPriorityFilter('all');
                                        setStatusFilter('all');
                                        setCategoryFilter('all');
                                    }}
                                    title="Clear all filters"
                                >
                                    <i className="ri-filter-off-line"></i>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Alerts List */}
                <div className="dashboard-alerts-list-container">
                    {paginatedAlerts.length === 0 ? (
                        <div className="dashboard-alerts-empty">
                            <i className="ri-alert-line"></i>
                            <h3>No alerts found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className="dashboard-alerts-list">
                            {paginatedAlerts.map((alert) => (
                                <div 
                                    key={alert.id} 
                                    className="dashboard-alert-card"
                                    onClick={() => handleAlertClick(alert)}
                                >
                                    <div className="dashboard-alert-main">
                                        <div className="dashboard-alert-icon">
                                            <i className={getCategoryIcon(alert.category)}></i>
                                        </div>
                                        <div className="dashboard-alert-content">
                                            <h4 className="dashboard-alert-title">{alert.title}</h4>
                                            <p className="dashboard-alert-description">{alert.description}</p>
                                            <div className="dashboard-alert-meta">
                                                <span className="dashboard-alert-source">
                                                    <i className="ri-server-line"></i>
                                                    {alert.source || 'System'}
                                                </span>
                                                {alert.created_by_name && (
                                                    <span className="dashboard-alert-assigned">
                                                        <i className="ri-user-line"></i>
                                                        {alert.created_by_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dashboard-alert-severity">
                                        <span
                                            className="dashboard-alert-severity-badge"
                                            style={{
                                                backgroundColor: `${getPriorityColor(alert.priority)}20`,
                                                color: getPriorityColor(alert.priority)
                                            }}
                                        >
                                            {alert.priority}
                                        </span>
                                    </div>
                                    <div className="dashboard-alert-status">
                                        <span
                                            className="dashboard-alert-status-badge"
                                            style={{
                                                backgroundColor: `${getStatusColor(alert.status)}20`,
                                                color: getStatusColor(alert.status)
                                            }}
                                        >
                                            {alert.status}
                                        </span>
                                    </div>
                                    <div className="dashboard-alert-category">
                                        <span className="dashboard-alert-category-badge">
                                            {alert.category}
                                        </span>
                                    </div>
                                    <div className="dashboard-alert-time">
                                        <span className="dashboard-alert-time-main">{getTimeAgo(alert.created_at)}</span>
                                        <span className="dashboard-alert-time-full">{formatDate(alert.created_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="dashboard-alerts-pagination">
                        <button
                            className="dashboard-alerts-pagination-btn"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <i className="ri-arrow-left-line"></i>
                            Previous
                        </button>
                        <div className="dashboard-alerts-pagination-info">
                            Page {currentPage} of {totalPages} ({filteredAlerts.length} alerts)
                        </div>
                        <button
                            className="dashboard-alerts-pagination-btn"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <i className="ri-arrow-right-line"></i>
                        </button>
                    </div>
                )}

                {/* Alert Detail Modal */}
                {isDetailModalOpen && selectedAlert && (
                    <div className="dashboard-alert-modal-overlay" onClick={closeModal}>
                        <div className="dashboard-alert-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="dashboard-alert-modal-header">
                                <div className="dashboard-alert-modal-header-content">
                                    <h3>{selectedAlert.title}</h3>
                                    <p>Alert Details and Information</p>
                                </div>
                                <button onClick={closeModal}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="dashboard-alert-modal-content">
                                <div className="dashboard-alert-modal-section">
                                    <h4>Alert Information</h4>
                                    <div className="dashboard-alert-modal-badges">
                                        <span 
                                            className="dashboard-alert-severity-badge"
                                            style={{
                                                backgroundColor: `${getPriorityColor(selectedAlert.priority)}20`,
                                                color: getPriorityColor(selectedAlert.priority)
                                            }}
                                        >
                                            {selectedAlert.priority} priority
                                        </span>
                                        <span 
                                            className="dashboard-alert-status-badge"
                                            style={{
                                                backgroundColor: `${getStatusColor(selectedAlert.status)}20`,
                                                color: getStatusColor(selectedAlert.status)
                                            }}
                                        >
                                            {selectedAlert.status}
                                        </span>
                                        <span className="dashboard-alert-category-badge">
                                            <i className={getCategoryIcon(selectedAlert.category)}></i>
                                            {selectedAlert.category}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="dashboard-alert-modal-section">
                                    <h4>Description</h4>
                                    <p>{selectedAlert.description}</p>
                                </div>
                                
                                {selectedAlert.message && (
                                    <div className="dashboard-alert-modal-section">
                                        <h4>Detailed Message</h4>
                                        <p>{selectedAlert.message}</p>
                                    </div>
                                )}
                                
                                <div className="dashboard-alert-modal-section">
                                    <h4>Details</h4>
                                    <div className="dashboard-alert-modal-info">
                                        <div className="dashboard-alert-modal-info-item">
                                            <span className="dashboard-alert-modal-info-label">Source:</span>
                                            <span>{selectedAlert.source || 'System'}</span>
                                        </div>
                                        <div className="dashboard-alert-modal-info-item">
                                            <span className="dashboard-alert-modal-info-label">Category:</span>
                                            <span>{selectedAlert.category}</span>
                                        </div>
                                        <div className="dashboard-alert-modal-info-item">
                                            <span className="dashboard-alert-modal-info-label">Type:</span>
                                            <span>{selectedAlert.type}</span>
                                        </div>
                                        <div className="dashboard-alert-modal-info-item">
                                            <span className="dashboard-alert-modal-info-label">Created:</span>
                                            <span>{formatDate(selectedAlert.created_at)}</span>
                                        </div>
                                        {selectedAlert.created_by_name && (
                                            <div className="dashboard-alert-modal-info-item">
                                                <span className="dashboard-alert-modal-info-label">Created by:</span>
                                                <span>{selectedAlert.created_by_name}</span>
                                            </div>
                                        )}
                                        {selectedAlert.assigned_to_name && (
                                            <div className="dashboard-alert-modal-info-item">
                                                <span className="dashboard-alert-modal-info-label">Assigned to:</span>
                                                <span>{selectedAlert.assigned_to_name}</span>
                                            </div>
                                        )}
                                        {selectedAlert.resolution_notes && (
                                            <div className="dashboard-alert-modal-info-item">
                                                <span className="dashboard-alert-modal-info-label">Resolution Notes:</span>
                                                <span>{selectedAlert.resolution_notes}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="dashboard-alert-modal-actions">
                                <button 
                                    className="dashboard-alert-modal-btn dashboard-alert-modal-btn-secondary"
                                    onClick={closeModal}
                                >
                                    <i className="ri-close-line"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default DashboardAlerts;
